// src/layouts/SalesLayout.jsx
import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo.jpeg";

/* ICONS */
import dashboardIcon from "../assets/dashboard-icon.svg";
import salesIcon from "../assets/transaction-icon.png";

const COLORS = {
  primary: "#0D47A1",
  red: "#D32F2F",
  bgCream: "#FFF6EA",
  textDark: "#1E1E1E",
  textGrey: "#757575",
  sidebarBg: "#FFFFFF",
  border: "#E5E7EB",
  activeBg: "#E3F2FD",
};

const DESKTOP_SIDEBAR = 280;
const MOBILE_SIDEBAR = 260; // Slightly wider for better text fit

const SalesLayout = ({ children }) => {
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  /* SCREEN DETECT */
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false); // Close mobile menu if resized to desktop
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const userName = user?.name || "Sales User";
  const userRole = role || "SALESMAN";

  return (
    <div style={styles.wrapper}>
      
      {/* MOBILE HEADER */}
      {isMobile && (
        <div style={styles.mobileHeader}>
          <button style={styles.menuBtn} onClick={() => setMobileOpen(true)}>☰</button>
          <div style={styles.mobileTitle}>Khata Saarthi</div>
        </div>
      )}

      {/* OVERLAY */}
      {isMobile && mobileOpen && (
        <div style={styles.overlay} onClick={() => setMobileOpen(false)} />
      )}

      {/* SIDEBAR */}
      <div
        style={{
          ...styles.sidebar,
          width: isMobile ? MOBILE_SIDEBAR : DESKTOP_SIDEBAR,
          left: 0,
          transform: isMobile
            ? mobileOpen ? "translateX(0)" : "translateX(-100%)"
            : "translateX(0)",
          transition: "transform 0.3s ease",
          zIndex: isMobile ? 1200 : 100, // Stays below Portals (9999)
        }}
      >
        {/* LOGO SECTION */}
        <div style={styles.sidebarTop}>
            <div style={styles.logoWrap}>
                <img src={logo} alt="" style={styles.logoImg} />
                <div>
                    <div style={styles.logoText}>Khata Saarthi</div>
                    <div style={styles.logoSub}>Sales Panel</div>
                </div>
            </div>

            {/* USER CARD */}
            <div style={styles.userCard}>
                <div style={styles.avatar}>{userName.charAt(0)}</div>
                <div style={{overflow: 'hidden'}}>
                    <div style={styles.userName}>{userName}</div>
                    <div style={styles.roleBadge}>{userRole}</div>
                </div>
            </div>

            {/* MENU ITEMS */}
            <div style={styles.menuList}>
                <MenuItem to="/" label="Dashboard" icon={dashboardIcon} onClick={() => setMobileOpen(false)} />
                <MenuItem to="/sales" label="Sales" icon={salesIcon} onClick={() => setMobileOpen(false)} />
            </div>
        </div>

        {/* BOTTOM SECTION */}
        <div style={styles.bottomSection}>
          <button style={styles.logoutBtn} onClick={handleLogout}>
            Logout
          </button>

          <div style={styles.footer}>
            <div>All rights reserved by</div>
            <a href="https://codesaarthi.com" target="_blank" rel="noreferrer" style={styles.link}>
              CodeSaarthi
            </a>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div
        style={{
          ...styles.main,
          marginLeft: isMobile ? 0 : DESKTOP_SIDEBAR,
          paddingTop: isMobile ? 76 : 20, // Add space for mobile header
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* MENU ITEM COMPONENT */
const MenuItem = ({ to, label, icon, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    style={({ isActive }) => ({
      ...styles.menuItem,
      background: isActive ? COLORS.activeBg : "transparent",
      color: isActive ? COLORS.primary : COLORS.textDark,
      fontWeight: isActive ? "600" : "400",
    })}
  >
    <div style={styles.itemLeft}>
      <img src={icon} alt="" style={styles.icon} />
      {label}
    </div>
  </NavLink>
);

/* IMPROVED STYLES */
const styles = {
  wrapper: {
    display: "flex",
    minHeight: "100vh",
    background: COLORS.bgCream,
    width: "100%",
  },

  mobileHeader: {
    height: 60,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    borderBottom: `1px solid ${COLORS.border}`,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100, // Below sidebar and portals
  },

  menuBtn: {
    fontSize: 24,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    marginRight: 15,
    padding: 0,
    color: COLORS.textDark,
  },

  mobileTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: COLORS.primary,
  },

  sidebar: {
    position: "fixed",
    top: 0,
    bottom: 0,
    background: COLORS.sidebarBg,
    padding: "20px 16px",
    borderRight: `1px solid ${COLORS.border}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between", // Pushes logout to bottom
    overflowY: "auto",
  },

  sidebarTop: {
    display: "flex",
    flexDirection: "column",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 1150,
    backdropFilter: "blur(2px)",
  },

  main: {
    flex: 1,
    minHeight: "100vh",
    transition: "margin-left 0.3s ease",
    width: "100%",
  },

  logoWrap: { display: "flex", alignItems: "center", gap: 12, marginBottom: 25 },
  logoImg: { width: 40, height: 40, borderRadius: 8 },
  logoText: { fontWeight: 800, fontSize: 16, color: COLORS.textDark, letterSpacing: -0.5 },
  logoSub: { fontSize: 11, color: COLORS.textGrey, marginTop: -2 },

  userCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px",
    background: COLORS.bgCream,
    borderRadius: 12,
    marginBottom: 20,
  },

  avatar: {
    width: 32,
    height: 32,
    fontSize: 14,
    borderRadius: "10px",
    background: COLORS.primary,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    flexShrink: 0,
  },

  userName: { fontWeight: 700, fontSize: 14, color: COLORS.textDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  roleBadge: { fontSize: 11, color: COLORS.red, fontWeight: 600, textTransform: "uppercase" },

  menuList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  menuItem: {
    padding: "12px 14px",
    borderRadius: 10,
    textDecoration: "none",
    display: "block",
    fontSize: 14,
    transition: "all 0.2s ease",
  },

  itemLeft: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },

  icon: { width: 18, height: 18, objectFit: "contain" },

  bottomSection: {
    marginTop: 40,
    borderTop: `1px solid ${COLORS.border}`,
    paddingTop: 20,
  },

  logoutBtn: {
    width: "100%",
    padding: "10px",
    borderRadius: 10,
    border: `2px solid ${COLORS.red}`,
    background: "transparent",
    color: COLORS.red,
    fontWeight: 700,
    cursor: "pointer",
    marginBottom: 15,
    fontSize: 14,
    transition: "all 0.2s ease",
  },

  footer: {
    fontSize: 11,
    textAlign: "center",
    color: COLORS.textGrey,
    lineHeight: 1.5,
  },

  link: {
    color: COLORS.primary,
    textDecoration: "none",
    fontWeight: 700,
  },
};

export default SalesLayout;